import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';
import { useSEO } from '../../utils/seo';

const AboutPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://okayama-pet-funeral.example.com';

  useSEO({
    title: '当院について｜岡山市のペット葬儀・火葬｜おかやまペット葬儀株式会社',
    description: '岡山市で20年以上の実績を持つペット葬儀サービス。代表挨拶、事業内容、施設のご案内。小鳥、ハムスター、猫、小型犬から大型犬まで対応。個別火葬、合同納骨、永代供養、仏具販売を提供。',
    keywords: '岡山市,ペット葬儀,会社概要,施設案内,火葬炉,納骨室,休憩室,ペット火葬,永代供養',
    canonical: `${siteUrl}/about`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${siteUrl}/about#webpage`,
      url: `${siteUrl}/about`,
      name: '当院について',
      description: '岡山市で20年以上の実績を持つペット葬儀サービス。代表挨拶、事業内容、施設のご案内。',
      mainEntity: {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'おかやまペット葬儀株式会社',
        founder: {
          '@type': 'Person',
          name: '岡山太郎',
          jobTitle: '代表取締役',
        },
        foundingDate: '2000',
        numberOfEmployees: 9,
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <PageHero title="当院について" subtitle="About Us" />

      {/* 代表挨拶セクション */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
            <div className="lg:col-span-2">
              <div className="rounded-3xl overflow-hidden shadow-lg sticky top-32">
                <img
                  src="https://public.readdy.ai/ai/img_res/63e0001fbf3732543b57b5640d97a422.jpg"
                  alt="代表取締役 岡山太郎"
                  className="w-full h-full object-cover object-top"
                />
                <div className="bg-white text-center py-4 px-6">
                  <p className="text-lg font-bold text-purple-600">代表取締役</p>
                  <p className="text-2xl font-serif font-bold text-gray-800 mt-1">
                    岡山 太郎
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <h1 className="text-3xl sm:text-4xl font-serif font-bold text-gray-800 mb-8">
                ご挨拶
              </h1>
              <div className="space-y-6 text-base leading-relaxed text-gray-700">
                <p>
                  おかやまペット葬儀株式会社のホームページをご覧いただき、誠にありがとうございます。代表取締役の岡山太郎と申します。
                </p>
                <p>
                  私たちは2000年の創業以来、岡山市を中心とした地域で、大切なペットとの最後のお別れをサポートしてまいりました。ペットは家族の一員であり、かけがえのない存在です。その大切な家族を失う悲しみは、計り知れないものがあります。
                </p>
                <p>
                  当社では、小鳥やハムスターなどの小動物から、猫、小型犬、大型犬まで、すべてのペットに対して心を込めた葬儀サービスを提供しております。個別火葬、合同納骨、永代供養、そして仏具の販売まで、ご家族のご希望に応じた最適なプランをご提案いたします。
                </p>
                <p>
                  スタッフ一同、ペットを愛する気持ちを大切に、丁寧で温かいサービスを心がけております。24時間365日対応しておりますので、突然のお別れの際にも、すぐにお力になれるよう体制を整えております。
                </p>
                <p>
                  大切なペットとの最後の時間を、心穏やかにお過ごしいただけるよう、私たちが精一杯サポートさせていただきます。どうぞお気軽にご相談ください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 事業内容セクション */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-purple-100 to-pink-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-800 mb-4">
              事業内容
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                icon: 'ri-fire-line',
                title: 'ペット火葬サービス',
                description:
                  '個別火葬と合同火葬をご用意。ご家族立ち会いのもと、丁寧に火葬いたします。',
                pets: ['大型犬', '小型犬', '猫', '小鳥', 'ハムスター'],
              },
              {
                icon: 'ri-building-line',
                title: '納骨・永代供養',
                description:
                  '美しい納骨室で、個別納骨または合同納骨をお選びいただけます。',
                pets: ['個別納骨棚', '合同納骨堂', '永代供養'],
              },
              {
                icon: 'ri-store-line',
                title: 'ペット仏具販売',
                description:
                  'おりん付き祭壇、遺骨カプセル、写真立てなど各種仏具を販売しております。',
                pets: ['祭壇', '遺骨カプセル', '写真立て', 'おりん'],
              },
            ].map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-20 h-20 bg-purple-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${service.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 text-center mb-4">
                  {service.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed text-center mb-6">
                  {service.description}
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {service.pets.map((pet, i) => (
                    <span
                      key={i}
                      className="bg-purple-100 text-purple-700 text-xs px-3 py-1 rounded-full"
                    >
                      {pet}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 施設写真セクション */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-800 mb-4">
              施設のご案内
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:gap-8">
            {[
              {
                title: '休憩室',
                description:
                  '柔らかいソファと温かい照明で、ゆっくりとお別れの時間をお過ごしいただけます。',
                image:
                  'https://readdy.ai/api/search-image?query=A%20peaceful%20and%20comfortable%20waiting%20room%20with%20soft%20sofas%2C%20warm%20ambient%20lighting%2C%20Japanese%20family%20sitting%20quietly%2C%20gentle%20and%20calming%20atmosphere%2C%20clean%20and%20organized%20space%2C%20warm%20wood%20tones%20and%20soft%20pastel%20colors%2C%20respectful%20memorial%20setting%2C%20simple%20background%2C%20photorealistic%20style%2C%20high%20quality%20interior%20photography&width=800&height=600&seq=facility-lounge&orientation=landscape',
              },
              {
                title: '納骨室',
                description:
                  '美しい木製の個別納骨棚と、合同納骨堂を完備。清潔で落ち着いた空間です。',
                image:
                  'https://readdy.ai/api/search-image?query=A%20serene%20and%20beautiful%20memorial%20room%20with%20elegant%20wooden%20shelving%20for%20individual%20pet%20urns%20and%20a%20communal%20memorial%20area%2C%20soft%20ambient%20lighting%2C%20peaceful%20atmosphere%2C%20clean%20and%20organized%20space%2C%20warm%20wood%20tones%2C%20respectful%20and%20dignified%20setting%2C%20simple%20background%20without%20text%20or%20signs%2C%20photorealistic%20style%2C%20high%20quality%20interior%20photography&width=800&height=600&seq=facility-memorial&orientation=landscape',
              },
              {
                title: '火葬炉',
                description:
                  '最新の設備を導入し、清潔で近代的な環境で丁寧に火葬を行います。',
                image:
                  'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/7b0d1be53b88bd27c2a9ffffec841993.jpeg',
              },
            ].map((facility, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg"
              >
                <div className="relative w-full" style={{ paddingBottom: '37.5%' }}>
                  <img
                    src={facility.image}
                    alt={facility.title}
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    {facility.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {facility.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
